/**
 * Locale Selector Component
 * 
 * Dropdown for users to select their preferred language.
 * Shows native language names for easy recognition.
 * 
 * @module components/LocaleSelector
 * @see docs/designs/LOCALIZATION_NORTH_STAR.md
 */

import React, { useCallback, useId } from 'react';
import { useI18n } from '../contexts/LocalizationContext';

// =============================================================================
// STYLES
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  
  label: {
    fontSize: '13px',
    color: 'var(--main-text-muted, #888)',
    whiteSpace: 'nowrap',
  },
  
  select: {
    appearance: 'none',
    background: 'var(--input-bg, #1a1f2e)',
    border: '1px solid var(--input-border, #2d3548)',
    borderRadius: 'var(--radius-sm, 6px)',
    color: 'var(--input-text, #fff)',
    fontSize: '13px',
    padding: '6px 32px 6px 12px',
    cursor: 'pointer',
    minWidth: '140px',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  
  selectFocused: {
    borderColor: 'var(--main-accent, #00d4ff)',
    boxShadow: '0 0 0 2px var(--main-accent-alpha, rgba(0, 212, 255, 0.2))',
  },
  
  chevron: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: 'var(--main-text-muted, #888)',
    fontSize: '10px',
  },
  
  // Compact variant for header
  selectCompact: {
    padding: '4px 28px 4px 10px',
    fontSize: '12px',
    minWidth: '120px',
  },
  
  // Icon-only variant
  iconButton: {
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 'var(--radius-sm, 6px)',
    color: 'var(--main-text, #fff)',
    cursor: 'pointer',
    padding: '6px 8px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    transition: 'background 0.15s ease, border-color 0.15s ease',
  },
  
  chevronOnDark: {
    color: 'var(--footer-text, #CBD5E1)',
    opacity: 0.85,
  },
};

// =============================================================================
// COMPONENT PROPS
// =============================================================================

export interface LocaleSelectorProps {
  /** Visual variant */
  variant?: 'default' | 'compact' | 'icon-only';
  /** Surface tone — use `on-dark` in footer / dark chrome bands */
  tone?: 'default' | 'on-dark';
  /** Show label text */
  showLabel?: boolean;
  /** Custom label text (overrides default) */
  label?: string;
  /** Additional CSS class */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** Callback when locale changes */
  onChange?: (locale: string) => void;
  /** Disable the selector */
  disabled?: boolean;
  /** aria-label for accessibility */
  ariaLabel?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Locale selector dropdown
 * 
 * @example
 * // In header
 * <LocaleSelector variant="compact" />
 * 
 * @example
 * // In settings page
 * <LocaleSelector showLabel label="Language" />
 * 
 * @example
 * // Icon-only for minimal UI
 * <LocaleSelector variant="icon-only" />
 */
export function LocaleSelector({
  variant = 'default',
  tone = 'default',
  showLabel = false,
  label,
  className,
  style,
  onChange,
  disabled = false,
  ariaLabel,
}: LocaleSelectorProps) {
  const { locale, setLocale, availableLocales, t, isLoading } = useI18n();
  const selectId = useId();
  
  const handleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLocale = e.target.value;
    setLocale(newLocale);
    onChange?.(newLocale);
  }, [setLocale, onChange]);
  
  // Find current locale info
  const currentLocale = availableLocales.find(l => l.code === locale);
  
  // Determine label text
  const labelText = label ?? t('shell.locale.label');
  
  // Accessibility label
  const accessibilityLabel = ariaLabel ?? labelText;
  
  // Icon-only variant shows just a globe icon
  if (variant === 'icon-only') {
    return (
      <div 
        className={className}
        style={{ ...styles.container, ...style }}
      >
        <label htmlFor={selectId} className="sr-only">
          {accessibilityLabel}
        </label>
        <div style={{ position: 'relative' }}>
          <select
            id={selectId}
            value={locale}
            onChange={handleChange}
            disabled={disabled || isLoading}
            aria-label={accessibilityLabel}
            style={{
              ...styles.iconButton,
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {availableLocales.map(loc => (
              <option key={loc.code} value={loc.code}>
                {loc.nativeName}
              </option>
            ))}
          </select>
          <span style={styles.chevron}>🌐</span>
        </div>
      </div>
    );
  }
  
  // Standard select variants
  const selectStyle: React.CSSProperties = {
    ...styles.select,
    ...(variant === 'compact' ? styles.selectCompact : {}),
    opacity: isLoading ? 0.6 : 1,
  };

  const chevronStyle: React.CSSProperties = {
    ...styles.chevron,
    ...(tone === 'on-dark' ? styles.chevronOnDark : {}),
  };
  
  const rootClassName = [
    'locale-selector',
    tone === 'on-dark' ? 'locale-selector--on-dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div 
      className={rootClassName}
      style={{ ...styles.container, ...style }}
    >
      {showLabel && (
        <label htmlFor={selectId} style={styles.label}>
          {labelText}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        <select
          id={selectId}
          value={locale}
          onChange={handleChange}
          disabled={disabled || isLoading}
          aria-label={showLabel ? undefined : accessibilityLabel}
          style={selectStyle}
        >
          {availableLocales.length === 0 ? (
            // Fallback while loading
            <option value={locale}>
              {currentLocale?.nativeName || locale}
            </option>
          ) : (
            availableLocales.map(loc => (
              <option key={loc.code} value={loc.code}>
                {/* Show: "Deutsch (German)" for clarity */}
                {loc.nativeName}
                {loc.nativeName !== loc.name && ` (${loc.name})`}
              </option>
            ))
          )}
        </select>
        
        <span className="locale-selector-chevron" style={chevronStyle} aria-hidden="true">
          ▾
        </span>
      </div>
    </div>
  );
}

// =============================================================================
// CONVENIENCE EXPORTS
// =============================================================================

/**
 * Compact locale selector for header/toolbar use
 */
export function LocaleSelectorCompact(props: Omit<LocaleSelectorProps, 'variant'>) {
  return <LocaleSelector variant="compact" {...props} />;
}

/**
 * Icon-only locale selector for minimal UI
 */
export function LocaleSelectorIcon(props: Omit<LocaleSelectorProps, 'variant'>) {
  return <LocaleSelector variant="icon-only" {...props} />;
}

export default LocaleSelector;
